import {axi} from '@shared/api';
import {validateData} from '@shared/utils';
import {
  Onboarding,
  OnboardingDetails,
  OnboardingType,
  UserOnboardingStatus,
} from '../types';
import {
  onboardingDetailsV8nSchema,
  v8nSchemaOfGetOnboardingsResponse,
  v8nSchemaOfGetUserOnboardingStatusesResponse,
} from './backendSchemas';
import {
  GetOnboardingsResponse,
  GetUserOnboardingStatusesResponse,
  OnboardingDetailsOnBackend,
} from './backendTypes';
import {
  mapOnboardingDetailsFromBackend,
  mapOnboardingFromBackend,
  mapUserOnboardingStatusFromBackend,
} from './dataMappers';

export const getOnboardings = async (): Promise<Onboarding[]> => {
  const response = await axi.get<GetOnboardingsResponse>('/admin/onboardings');

  try {
    validateData(
      response.data,
      v8nSchemaOfGetOnboardingsResponse,
      'getOnboardings',
    );
  } catch {
    //
  }

  return (response.data ?? []).map(item => mapOnboardingFromBackend(item));
};

export const getOnboarding = async (
  type: OnboardingType,
): Promise<OnboardingDetails> => {
  const response = await axi.get<OnboardingDetailsOnBackend>(
    `/admin/onboardings/${type}`,
  );

  try {
    validateData(response.data, onboardingDetailsV8nSchema, 'getOnboarding');
  } catch {
    //
  }

  return mapOnboardingDetailsFromBackend(response.data);
};

export const updateOnboarding = async (
  type: OnboardingType,
  config: string,
): Promise<void> => {
  await axi.put(`/admin/onboardings/${type}`, {config});
};

export interface AddOnboardingGeoConfigParams {
  type: OnboardingType;
  countryCode: string;
  config: string;
}

export const addOnboardingGeoConfig = async ({
  type,
  countryCode,
  config,
}: AddOnboardingGeoConfigParams): Promise<void> => {
  await axi.post(`/admin/onboardings/${type}/geo-configs`, {
    country_code: countryCode,
    config,
  });
};

export const getUserOnboardingStatuses = async (
  userId: string,
): Promise<UserOnboardingStatus[]> => {
  const response = await axi.get<GetUserOnboardingStatusesResponse>(
    `/admin/onboardings/users/${userId}`,
  );

  try {
    validateData(
      response.data,
      v8nSchemaOfGetUserOnboardingStatusesResponse,
      'getUserOnboardingStatuses',
    );
  } catch {
    //
  }

  return (response.data?.onboardings ?? []).map(item =>
    mapUserOnboardingStatusFromBackend(item),
  );
};

export interface ResetUserOnboardingStatusesParams {
  userId: string;
  types: OnboardingType[];
}

export const resetUserOnboardingStatuses = async ({
  userId,
  types,
}: ResetUserOnboardingStatusesParams): Promise<void> => {
  await axi.post(`/admin/onboardings/users/${userId}/reset`, {types});
};

export interface RemoveOnboardingGeoConfigParams {
  type: OnboardingType;
  countryCode: string;
}

export const removeOnboardingGeoConfig = async ({
  type,
  countryCode,
}: RemoveOnboardingGeoConfigParams): Promise<void> => {
  await axi.delete(`/admin/onboardings/${type}/geo-configs/${countryCode}`);
};

export interface AddOnboardingExtDataConfigParams {
  type: OnboardingType;
  extName: string;
  extValue: string;
  config: string;
  priority: number;
}

export interface UpdateOnboardingExtDataConfigParams {
  type: OnboardingType;
  configId: string;
  config: string;
  priority: number;
}

export interface RemoveOnboardingExtDataConfigParams {
  type: OnboardingType;
  configId: string;
}

export const addOnboardingExtDataConfig = async ({
  type,
  extName,
  extValue,
  config,
  priority,
}: AddOnboardingExtDataConfigParams): Promise<void> => {
  await axi.post(`/admin/onboardings/${type}/ext-data-configs`, {
    ext_name: extName,
    ext_value: extValue,
    config,
    priority,
  });
};

export const updateOnboardingExtDataConfig = async ({
  type,
  configId,
  config,
  priority,
}: UpdateOnboardingExtDataConfigParams): Promise<void> => {
  await axi.put(`/admin/onboardings/${type}/ext-data-configs/${configId}`, {
    config,
    priority,
  });
};

export const removeOnboardingExtDataConfig = async ({
  type,
  configId,
}: RemoveOnboardingExtDataConfigParams): Promise<void> => {
  await axi.delete(`/admin/onboardings/${type}/ext-data-configs/${configId}`);
};
